import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"

/**
 * The session for the current request, resolved on the server.
 *
 * The dashboard's chrome — sidebar, tabs, page header — is permission-filtered,
 * so it cannot draw until it knows who you are. Resolved on the client that
 * meant a round-trip with a full-page skeleton in front of it on every cold
 * load. Resolved here it is already in the first HTML, and the chrome paints
 * real on the first frame.
 *
 * Returns null rather than throwing when there is no session: an unauthenticated
 * visitor is an ordinary case that the gate handles by redirecting to login.
 */
export const readDashboardSessionForRequest = createServerFn({ method: "GET" }).handler(
  async (): Promise<string | null> => {
    try {
      const request = getRequest()
      const { readDashboardSession, dashboardSessionPublicPayload } = await import(
        "@playbrain/auth/server/dashboardSession"
      )
      const session = await readDashboardSession(request)
      if (!session) return null

      // Permissions live in SQL, not in the cookie — the same hydration the
      // client-side session endpoint performs, so both paths agree.
      const { withDashboardSqlProfile } = await import("@api/features/dashboard-profile/dashboardProfile")
      const user = await withDashboardSqlProfile(session.user)
      /**
       * Serialized here rather than returned as an object: `AuthUser` carries an
       * open claim map, which the server-function boundary cannot prove is
       * serializable. The caller parses it back into the session shape.
       */
      return JSON.stringify({ ...dashboardSessionPublicPayload(session), user })
    } catch (error) {
      // A failure here must not break the page: the client refresh will retry,
      // and the gate falls back to its own resolution.
      console.error("Could not resolve the dashboard session on the server", error)
      return null
    }
  }
)
